import { ErrorDisplay, Spinner } from '@automata/ui';
import { env, logger, useTabState } from '@automata/utils';
import { useGetWorkflow } from '@automata/wfb-api';
import { useRouter } from 'next/router';
import { StrictMode, useCallback, useEffect } from 'react';
import { Provider } from 'react-redux';
import { useToggle } from 'usehooks-ts';

import { MainPanel } from './MainPanel';
import { MainToolbar } from './MainToolbar';
import { useWorkflowStore } from './useWorkflowStore';
import { useInitializeConfirmNavigateAway } from '../../hooks/useInitializeConfirmNavigateAway';
import { useInitializeScripts } from '../../hooks/useInitializeScripts';
import { useInitializeWorkcellAndDrivers } from '../../hooks/useInitializeWorkcellAndDrivers';
import { useInitializeWorkflow } from '../../hooks/useInitializeWorkflow';
import {
  type User,
  useInitializeWorkflowAuthor,
} from '../../hooks/useInitializeWorkflowAuthor';
import { TabId } from '../../types';
import { ErrorBoundary } from '../ErrorBoundary';
import IsPlanWorkflowModalOpenContextProvider from '../PreviewPanel/IsPlanWorkflowModalOpenContext/Provider';
import { SchedulerVersionModal } from '../SchedulerVersionModal';

export type Props = {
  workflowId?: string;
  user?: User;
};

export const CanvasAppInner = ({ workflowId, user }: Props): JSX.Element => {
  useInitializeConfirmNavigateAway();

  const [activeTabId, setActiveTabId] = useTabState<TabId>('build', [
    'build',
    'preview',
  ]);

  const handleClickReturnToWorkflow = useCallback(
    () => setActiveTabId('build'),
    [setActiveTabId]
  );

  const { getScriptsInfoError, getScriptsInfoIsLoading } =
    useInitializeScripts();

  const isExistingWorkflow = Boolean(workflowId);

  const {
    data: getWorkflowData,
    error: getWorkflowError,
    fetchStatus: getWorkflowFetchStatus,
  } = useGetWorkflow(
    { pathParams: { id: workflowId ?? '' } },
    { enabled: isExistingWorkflow }
  );

  const { getWorkcellIsLoading, getWorkcellError } =
    useInitializeWorkcellAndDrivers(getWorkflowData, isExistingWorkflow);

  const {
    schedulerVersionResolutionPrompt,
    isLoading: isLoadingInitialWorkflow,
    error: workflowInitializationError,
  } = useInitializeWorkflow(
    getWorkflowData,
    getWorkflowFetchStatus === 'fetching'
  );

  useInitializeWorkflowAuthor(user, isExistingWorkflow);

  const [
    isDescriptionPanelExpanded,
    toggleDescriptionPanelExpanded,
    setDescriptionPanelExpanded,
  ] = useToggle(false);

  const hideDescriptionPanel = useCallback(
    () => setDescriptionPanelExpanded(false),
    [setDescriptionPanelExpanded]
  );

  if (getWorkcellError) {
    return (
      <ErrorDisplay
        error={getWorkcellError}
        userMessage="Failed to load workcell"
        logToConsole
      />
    );
  }

  if (getWorkflowError) {
    return (
      <ErrorDisplay
        error={getWorkflowError}
        userMessage="Failed to load workflow"
        logToConsole
      />
    );
  }

  if (getScriptsInfoError || workflowInitializationError) {
    return (
      <ErrorDisplay
        error={getScriptsInfoError || workflowInitializationError}
        userMessage="Failed to initialize workflow"
        logToConsole
      />
    );
  }

  if (
    getWorkflowFetchStatus === 'fetching' ||
    (getWorkcellIsLoading && !isExistingWorkflow) ||
    getScriptsInfoIsLoading ||
    isLoadingInitialWorkflow
  ) {
    return <Spinner />;
  }

  return (
    <>
      <SchedulerVersionModal
        currentVersion={schedulerVersionResolutionPrompt?.currentVersion}
        defaultSelectedVersion={
          schedulerVersionResolutionPrompt?.defaultSelectedVersion
        }
        onConfirm={schedulerVersionResolutionPrompt?.onConfirm}
      />
      <MainToolbar
        isExistingWorkflow={isExistingWorkflow}
        toggleDescriptionPanelExpanded={toggleDescriptionPanelExpanded}
        workflowId={workflowId}
        activeTabId={activeTabId}
        onChangeTab={setActiveTabId}
      />
      <MainPanel
        hideDescriptionPanel={hideDescriptionPanel}
        isDescriptionPanelExpanded={isDescriptionPanelExpanded}
        workflowId={workflowId}
        activeTabId={activeTabId}
        onClickReturnToWorkflow={handleClickReturnToWorkflow}
      />
    </>
  );
};

const CanvasApp = ({ workflowId, user }: Props) => {
  const router = useRouter();
  const store = useWorkflowStore(router);

  useEffect(() => {
    logger.log(`current version is ${env('NEXT_PUBLIC_VERSION')}`);
  }, []);

  return (
    <StrictMode>
      <ErrorBoundary>
        <IsPlanWorkflowModalOpenContextProvider>
          <Provider store={store}>
            <CanvasAppInner workflowId={workflowId} user={user} />
          </Provider>
        </IsPlanWorkflowModalOpenContextProvider>
      </ErrorBoundary>
    </StrictMode>
  );
};

export default CanvasApp;
